import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { MapPin, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export const BranchLocationSettings = () => {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(null);

  const load = () => api.get("/branches").then((r) => setRows(r.data.map((b) => ({ id: b.id, name: b.name || "", address: b.address || "", maps_link: b.maps_link || "" })))).catch(() => {});
  useEffect(() => { load(); }, []);

  const edit = (id, k, v) => setRows((rs) => rs.map((b) => (b.id === id ? { ...b, [k]: v } : b)));
  const save = async (b) => {
    setSaving(b.id);
    try {
      await api.put(`/branches/${b.id}/location`, { name: b.name, address: b.address, maps_link: b.maps_link });
      toast.success(`${b.name} location saved`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(null); }
  };

  return (
    <div className="space-y-4" data-testid="branch-location-settings">
      <div>
        <h2 className="font-display text-xl flex items-center gap-2"><MapPin size={18} className="text-primary" /> Branch Location Settings</h2>
        <p className="text-sm text-muted-foreground">Name, address and Google Maps link shown to customers on the online booking branch screen.</p>
      </div>
      {rows.map((b) => (
        <Card key={b.id} className="p-5 space-y-3" data-testid={`branch-loc-card-${b.id}`}>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor={`bl-name-${b.id}`}>Branch Name</Label>
              <Input id={`bl-name-${b.id}`} value={b.name} onChange={(e) => edit(b.id, "name", e.target.value)} data-testid={`branch-loc-name-${b.id}`} className="mt-1" />
            </div>
            <div>
              <Label htmlFor={`bl-map-${b.id}`}>Google Maps / Location URL</Label>
              <div className="flex gap-2 mt-1">
                <Input id={`bl-map-${b.id}`} value={b.maps_link} placeholder="https://maps.app.goo.gl/..." onChange={(e) => edit(b.id, "maps_link", e.target.value)} data-testid={`branch-loc-map-${b.id}`} />
                {b.maps_link && <Button type="button" variant="outline" size="icon" onClick={() => window.open(b.maps_link, "_blank", "noopener")} data-testid={`branch-loc-open-${b.id}`} title="Open link"><ExternalLink size={14} /></Button>}
              </div>
            </div>
          </div>
          <div>
            <Label htmlFor={`bl-addr-${b.id}`}>Full Address</Label>
            <Textarea id={`bl-addr-${b.id}`} rows={2} value={b.address} onChange={(e) => edit(b.id, "address", e.target.value)} data-testid={`branch-loc-address-${b.id}`} className="mt-1" />
          </div>
          <div className="flex justify-end">
            <Button onClick={() => save(b)} disabled={saving === b.id || !b.name.trim()} data-testid={`branch-loc-save-${b.id}`}>{saving === b.id ? "Saving…" : "Save"}</Button>
          </div>
        </Card>
      ))}
      {!rows.length && <p className="text-sm text-muted-foreground">No branches found.</p>}
    </div>
  );
};
